import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Globe, Lock, Award, MapPin, Briefcase, Plane, Key } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const Home = () => {
  const { t, language } = useLanguage();

  const seo = {
    fr: {
      title: "JSX-conseils et Henry & Gaby – Private Business Club en aviation & Cabinet de conseil",
      description: "Henry & Gaby est un club privé international dédié à l'aviation, aux événements exclusifs et aux transactions haut de gamme, reliant décideurs, investisseurs et maisons de prestige.",
      h1: "Henry & Gaby – Private Business Club international"
    },
    en: {
      title: "JSX-conseils and Henry & Gaby – Private Business Club in Aviation & Consulting Firm",
      description: "Henry & Gaby is an international private club dedicated to aviation, exclusive events and high-end transactions, connecting decision-makers, investors and prestigious houses.",
      h1: "Henry & Gaby – International Private Business Club"
    }
  };

  const currentSeo = seo[language] || seo.fr;

  return (
    <div>
      <Helmet>
        <title>{currentSeo.title}</title>
        <meta name="description" content={currentSeo.description} />
        <link rel="canonical" href="https://henry-and-gaby.com/" />
        <meta property="og:title" content={currentSeo.title} />
        <meta property="og:description" content={currentSeo.description} />
        <meta property="og:url" content="https://henry-and-gaby.com/" />
        <meta property="og:type" content="website" />
      </Helmet>
      {/* Hero Section */}
      <section style={{
        position: 'relative',
        height: '600px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          inset: 0,
          zIndex: 0
        }}>
          <img
            src="https://images.unsplash.com/photo-1474302770737-173ee21bab63?w=1920&q=80"
            alt="Private jet"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to right, rgba(255, 254, 242, 0.95), rgba(255, 254, 242, 0.7))'
          }} />
        </div>

        <div className="container" style={{
          position: 'relative',
          zIndex: 1,
          textAlign: 'left',
          maxWidth: '700px'
        }}>
          <p className="body-large" style={{
            color: 'var(--text-secondary)',
            marginBottom: '16px',
            letterSpacing: '1px'
          }}>
            JSX International et Henry & Gaby
          </p>
          <h1 className="hero-large" style={{
            marginBottom: '24px',
            maxWidth: '600px'
          }}>
            {currentSeo.h1}
          </h1>
          <p className="body-large" style={{
            color: 'var(--text-secondary)',
            marginBottom: '40px'
          }}>
            {t.home.heroDescription}
          </p>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <Link to="/network" className="btn-primary">
              {t.home.ctaPrimary}
              <ArrowRight size={16} />
            </Link>
            <Link to="/contact" className="btn-secondary">
              {t.home.ctaSecondary}
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <h2 className="hero-medium" style={{ marginBottom: '32px', maxWidth: '800px' }}>
            {t.home.aboutTitle}
          </h2>
          <div className="grid-two-column" style={{ marginBottom: '80px' }}>
            <div>
              <p className="body-large" style={{ marginBottom: '24px' }}>
                {t.home.aboutP1}
              </p>
              <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
                {t.home.aboutP2}
              </p>
            </div>
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_global-advisory-1/artifacts/y00f7xzw_JSX%20CONSEILS%20ImageA%201.jpg"
                alt="Luxury private club interior"
                style={{
                  width: '100%',
                  height: '400px',
                  objectFit: 'cover'
                }}
              />
            </div>
          </div>

          {/* Features Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '40px'
          }}>
            <div>
              <div style={{
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px',
                border: '1px solid var(--border-light)'
              }}>
                <Shield size={24} color="var(--text-primary)" />
              </div>
              <h3 className="heading-2" style={{ marginBottom: '8px' }}>
                {t.home.feature1}
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                {t.home.feature1Desc}
              </p>
            </div>

            <div>
              <div style={{
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px',
                border: '1px solid var(--border-light)'
              }}>
                <Globe size={24} color="var(--text-primary)" />
              </div>
              <h3 className="heading-2" style={{ marginBottom: '8px' }}>
                {t.home.feature2}
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                {t.home.feature2Desc}
              </p>
            </div>

            <div>
              <div style={{
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px',
                border: '1px solid var(--border-light)'
              }}>
                <Lock size={24} color="var(--text-primary)" />
              </div>
              <h3 className="heading-2" style={{ marginBottom: '8px' }}>
                {t.home.feature3}
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                {t.home.feature3Desc}
              </p>
            </div>

            <div>
              <div style={{
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px',
                border: '1px solid var(--border-light)'
              }}>
                <Award size={24} color="var(--text-primary)" />
              </div>
              <h3 className="heading-2" style={{ marginBottom: '8px' }}>
                {t.home.feature4}
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                {t.home.feature4Desc}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding-small">
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 className="hero-medium" style={{ marginBottom: '24px' }}>
            {t.membership.quote}
          </h2>
          <Link to="/contact" className="btn-primary">
            {t.membership.cta}
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;